// src/pages/reports/PnlReportPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  Filter,
  Download,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import { getPnlFilters, getPnlReport } from '../../services/api/pnlReport';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/currencyUtil';
import styles from './PnlReportPage.module.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtAmt = (n) => formatCurrency(Math.abs(n));
const isNeg = (n) => n < 0;

const AmountCell = ({ value, deduction = false, bold = false, large = false, colored = false }) => {
  const neg = deduction || isNeg(value);
  const display = deduction ? `(${fmtAmt(value)})` : isNeg(value) ? `(${fmtAmt(value)})` : fmtAmt(value);
  return (
    <span
      className={[
        styles.amountCell,
        neg ? styles.negativeValue : '',
        bold ? styles.bold : '',
        large ? styles.largeFigure : '',
        colored && !neg ? styles.profitColor : '',
        colored && neg ? styles.lossColor : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {display}
    </span>
  );
};

// ─── P&L Summary Table (single-period vertical layout) ───────────────────────

const PnlSummaryTable = ({ data, showEcomSection, useStockCogs, t }) => {
  const { revenue, cogs, grossProfit, expenses, courierCharges, posFees, totalExpenses, netProfit } = data;

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.pnlTable}>
        <thead>
          <tr>
            <th className={styles.labelCol}>{t('report.pnlReport.title')}</th>
            <th className={styles.amountCol}>{t('report.value')}</th>
          </tr>
        </thead>
        <tbody>
          {/* ── REVENUE ── */}
          <tr className={styles.sectionHeader}>
            <td colSpan={2}>{t('report.pnlReport.revenue')}</td>
          </tr>
          {/* Ecom Sales sub-group — only shown when ecom activity exists */}
          {showEcomSection && (
            <>
              <tr>
                <td className={styles.indented}><strong>{t('report.pnlReport.ecomSales')}</strong></td>
                <td></td>
              </tr>
              <tr>
                <td className={styles.doubleIndented}>{t('report.pnlReport.realizedSales')}</td>
                <td><AmountCell value={revenue.realizedSales} /></td>
              </tr>
              <tr>
                <td className={styles.doubleIndented}>{t('report.pnlReport.unrealizedSales')}</td>
                <td><AmountCell value={revenue.unrealizedSales} /></td>
              </tr>
              <tr>
                <td className={styles.doubleIndented}>{t('report.pnlReport.salesReturns')}</td>
                <td><AmountCell value={revenue.salesReturns} deduction /></td>
              </tr>
            </>
          )}
          {/* POS */}
          <tr>
            <td className={styles.indented}>{t('report.pnlReport.posSales')}</td>
            <td><AmountCell value={revenue.posSales} /></td>
          </tr>
          <tr>
            <td className={styles.indented}>{t('report.pnlReport.lessPosReturns')}</td>
            <td><AmountCell value={revenue.posReturns} deduction /></td>
          </tr>
          <tr className={styles.subtotal}>
            <td>{t('report.pnlReport.netRevenue')}</td>
            <td><AmountCell value={revenue.netRevenue} bold /></td>
          </tr>

          {/* ── COGS ── */}
          <tr className={styles.sectionHeader}>
            <td colSpan={2}>{t('report.pnlReport.cogs')}</td>
          </tr>
          {useStockCogs ? (
            <>
              <tr>
                <td className={styles.indented}>{t('report.pnlReport.openingStock')}</td>
                <td><AmountCell value={cogs.openingStock} /></td>
              </tr>
              <tr>
                <td className={styles.indented}>{t('report.pnlReport.addPurchases')}</td>
                <td><AmountCell value={cogs.purchases} /></td>
              </tr>
              <tr>
                <td className={styles.indented}>{t('report.pnlReport.lessClosingStock')}</td>
                <td><AmountCell value={cogs.closingStock} deduction /></td>
              </tr>
            </>
          ) : (
            <tr>
              <td className={styles.indented}>{t('report.pnlReport.posProductCost')}</td>
              <td><AmountCell value={cogs.posProductCost} /></td>
            </tr>
          )}
          <tr className={styles.subtotal}>
            <td>{t('report.pnlReport.cogsTotal')}</td>
            <td><AmountCell value={cogs.cogs} bold /></td>
          </tr>

          {/* ── GROSS PROFIT ── */}
          <tr className={styles.grossProfitRow}>
            <td>{t('report.pnlReport.grossProfit')}</td>
            <td>
              <AmountCell value={grossProfit} bold colored />
            </td>
          </tr>

          {/* ── OPERATING EXPENSES ── */}
          <tr className={styles.sectionHeader}>
            <td colSpan={2}>{t('report.pnlReport.operatingExpenses')}</td>
          </tr>
          {courierCharges > 0 && (
            <tr>
              <td className={styles.indented}>{t('report.pnlReport.courierCharges')}</td>
              <td><AmountCell value={courierCharges} /></td>
            </tr>
          )}
          {posFees > 0 && (
            <tr>
              <td className={styles.indented}>{t('report.pnlReport.posFees')}</td>
              <td><AmountCell value={posFees} /></td>
            </tr>
          )}
          {expenses.map((cat) => (
            <React.Fragment key={cat.categoryId}>
              <tr>
                <td className={styles.indented}>{cat.categoryName}</td>
                <td><AmountCell value={cat.amount} /></td>
              </tr>
              {cat.children.map((child) => (
                <tr key={child.categoryId}>
                  <td className={styles.doubleIndented}>{child.categoryName}</td>
                  <td><AmountCell value={child.amount} /></td>
                </tr>
              ))}
            </React.Fragment>
          ))}
          <tr className={styles.subtotal}>
            <td>{t('report.pnlReport.totalExpenses')}</td>
            <td><AmountCell value={totalExpenses} bold /></td>
          </tr>

          {/* ── NET PROFIT ── */}
          <tr className={styles.netProfitRow}>
            <td>{netProfit >= 0 ? t('report.pnlReport.netProfit') : t('report.pnlReport.netLoss')}</td>
            <td>
              <AmountCell value={netProfit} bold large colored />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ─── Collect all unique category names from expense trees across periods ──────

const buildMasterExpenseRows = (periodsOrData) => {
  // Always include courier charges row first
  const rows = [{ key: '__courier__', label: null, isCourier: true }];
  const seen = new Set();
  const addCat = (cat) => {
    if (!seen.has(cat.categoryId)) {
      seen.add(cat.categoryId);
      rows.push({ key: `cat-${cat.categoryId}`, categoryId: cat.categoryId, label: cat.categoryName, indent: 1 });
      cat.children.forEach((child) => {
        if (!seen.has(child.categoryId)) {
          seen.add(child.categoryId);
          rows.push({ key: `cat-${child.categoryId}`, categoryId: child.categoryId, label: child.categoryName, indent: 2 });
        }
      });
    }
  };
  periodsOrData.forEach((p) => (p.expenses || []).forEach(addCat));
  return rows;
};

const getExpenseAmount = (expenses, categoryId) => {
  for (const cat of expenses) {
    if (cat.categoryId === categoryId) return cat.amount;
    for (const child of cat.children) {
      if (child.categoryId === categoryId) return child.amount;
    }
  }
  return 0;
};

// ─── P&L Grouped Table (periods as columns) ───────────────────────────────────

const PnlGroupedTable = ({ data, t }) => {
  const { periods, totals, showEcomSection, useStockCogs } = data;
  const allData = [...periods, { ...totals, label: t('report.pnlReport.total'), isTotal: true }];

  const masterExpenseRows = buildMasterExpenseRows(allData);
  const cols = allData;

  const Row = ({ labelKey, getValue, bold, deduction, sectionHeader, grossProfit: gp, netProfit: np }) => {
    if (sectionHeader) {
      return (
        <tr className={styles.sectionHeader}>
          <td>{t(labelKey)}</td>
          {cols.map((_, i) => <td key={i}></td>)}
        </tr>
      );
    }
    return (
      <tr className={gp ? styles.grossProfitRow : np ? styles.netProfitRow : bold ? styles.subtotal : ''}>
        <td className={deduction ? styles.indented : gp || np ? '' : ''}>{t(labelKey)}</td>
        {cols.map((col, i) => {
          const val = getValue(col);
          return (
            <td key={i} className={col.isTotal ? styles.totalColumn : ''}>
              <AmountCell value={val} bold={bold || gp || np} deduction={deduction} colored={gp || np} />
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className={styles.groupedTableWrapper}>
      <table className={styles.pnlTable}>
        <thead>
          <tr>
            <th className={styles.rowLabelCol}></th>
            {cols.map((col, i) => (
              <th key={i} className={[styles.periodCol, col.isTotal ? styles.totalColumn : ''].join(' ')}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Revenue section */}
          <Row labelKey="report.pnlReport.revenue" sectionHeader getValue={() => null} />
          {/* Ecom Sales sub-group — only shown when ecom activity exists */}
          {showEcomSection && (
            <>
              <tr>
                <td className={styles.indented}><strong>{t('report.pnlReport.ecomSales')}</strong></td>
                {cols.map((_, i) => <td key={i}></td>)}
              </tr>
              <Row labelKey="report.pnlReport.realizedSales" getValue={(c) => c.revenue?.realizedSales ?? 0} />
              <Row labelKey="report.pnlReport.unrealizedSales" getValue={(c) => c.revenue?.unrealizedSales ?? 0} />
              <Row labelKey="report.pnlReport.salesReturns" deduction getValue={(c) => c.revenue?.salesReturns ?? 0} />
            </>
          )}
          <Row labelKey="report.pnlReport.posSales" getValue={(c) => c.revenue?.posSales ?? 0} />
          <Row labelKey="report.pnlReport.lessPosReturns" deduction getValue={(c) => c.revenue?.posReturns ?? 0} />
          <Row labelKey="report.pnlReport.netRevenue" bold getValue={(c) => c.revenue?.netRevenue ?? 0} />

          {/* COGS section */}
          <Row labelKey="report.pnlReport.cogs" sectionHeader getValue={() => null} />
          {useStockCogs ? (
            <>
              <Row labelKey="report.pnlReport.openingStock" getValue={(c) => c.cogs?.openingStock ?? 0} />
              <Row labelKey="report.pnlReport.addPurchases" getValue={(c) => c.cogs?.purchases ?? 0} />
              <Row labelKey="report.pnlReport.lessClosingStock" deduction getValue={(c) => c.cogs?.closingStock ?? 0} />
            </>
          ) : (
            <Row labelKey="report.pnlReport.posProductCost" getValue={(c) => c.cogs?.posProductCost ?? 0} />
          )}
          <Row labelKey="report.pnlReport.cogsTotal" bold getValue={(c) => c.cogs?.cogs ?? 0} />

          {/* Gross Profit */}
          <Row labelKey="report.pnlReport.grossProfit" grossProfit getValue={(c) => c.grossProfit ?? 0} />

          {/* Expenses section */}
          <Row labelKey="report.pnlReport.operatingExpenses" sectionHeader getValue={() => null} />
          {masterExpenseRows.map((er) => {
            if (er.isCourier) {
              return (
                <tr key="courier">
                  <td className={styles.indented}>{t('report.pnlReport.courierCharges')}</td>
                  {cols.map((col, i) => (
                    <td key={i} className={col.isTotal ? styles.totalColumn : ''}>
                      <AmountCell value={col.courierCharges ?? 0} />
                    </td>
                  ))}
                </tr>
              );
            }
            return (
              <tr key={er.key}>
                <td className={er.indent === 2 ? styles.doubleIndented : styles.indented}>{er.label}</td>
                {cols.map((col, i) => (
                  <td key={i} className={col.isTotal ? styles.totalColumn : ''}>
                    <AmountCell value={getExpenseAmount(col.expenses || [], er.categoryId)} />
                  </td>
                ))}
              </tr>
            );
          })}
          {/* POS Fees row */}
          <tr key="posFees">
            <td className={styles.indented}>{t('report.pnlReport.posFees')}</td>
            {cols.map((col, i) => (
              <td key={i} className={col.isTotal ? styles.totalColumn : ''}>
                <AmountCell value={col.posFees ?? 0} />
              </td>
            ))}
          </tr>
          <Row labelKey="report.pnlReport.totalExpenses" bold getValue={(c) => c.totalExpenses ?? 0} />

          {/* Net Profit */}
          <Row labelKey="report.pnlReport.netProfit" netProfit getValue={(c) => c.netProfit ?? 0} />
        </tbody>
      </table>
    </div>
  );
};

// ─── Main Page Component ───────────────────────────────────────────────────────

const PnlReportPage = () => {
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  // Filter options
  const [datePresets, setDatePresets] = useState({});
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);

  // Filter state
  const [groupBy, setGroupBy] = useState('none');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('thisMonth');
  const [showFilters, setShowFilters] = useState(true);

  // Report data
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Applied filters (used for display in report header)
  const [appliedFilters, setAppliedFilters] = useState(null);

  // Load filter options on mount
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getPnlFilters();
        setDatePresets(data.dateRangePresets || {});
      } catch {
        showError(t('report.pnlReport.failedToLoadFilters'));
      } finally {
        setIsLoadingFilters(false);
      }
    };
    load();
  }, []);

  // Apply default preset when presets load
  useEffect(() => {
    if (datePresets.thisMonth) {
      setDateFrom(datePresets.thisMonth.from);
      setDateTo(datePresets.thisMonth.to);
    }
  }, [datePresets]);

  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    if (presetKey !== 'custom' && datePresets[presetKey]) {
      setDateFrom(datePresets[presetKey].from);
      setDateTo(datePresets[presetKey].to);
    }
  };

  const handleApply = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setIsLoading(true);
    setReportData(null);
    try {
      const data = await getPnlReport({ dateFrom, dateTo, groupBy });
      setReportData(data);
      setAppliedFilters({ dateFrom, dateTo, groupBy });
    } catch (err) {
      showError(t('report.pnlReport.failedToLoad'));
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo, groupBy]);

  const handleReset = () => {
    setGroupBy('none');
    setSelectedPreset('thisMonth');
    if (datePresets.thisMonth) {
      setDateFrom(datePresets.thisMonth.from);
      setDateTo(datePresets.thisMonth.to);
    }
    setReportData(null);
    setAppliedFilters(null);
  };

  // ── Excel Export ───────────────────────────────────────────────────────────
  const handleExport = async () => {
    if (!reportData) return;
    setIsExporting(true);
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Profit & Loss');

      const groupLabel =
        appliedFilters.groupBy === 'none'
          ? t('report.pnlReport.groupByNone')
          : appliedFilters.groupBy === 'month'
          ? t('report.pnlReport.groupByMonth')
          : t('report.pnlReport.groupByQuarter');

      // Title rows
      ws.addRow([t('report.pnlReport.title')]);
      ws.addRow([`${groupLabel} | ${appliedFilters.dateFrom} – ${appliedFilters.dateTo}`]);
      ws.addRow([]);

      const headerStyle = { font: { bold: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE9ECEF' } } };
      const sectionStyle = { font: { bold: true, size: 10 }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE9ECEF' } } };

      const { showEcomSection, useStockCogs } = reportData;

      if (reportData.groupBy === 'none') {
        const { data } = reportData;
        ws.addRow(['Description', 'Amount']).eachCell((c) => Object.assign(c, headerStyle));

        const addRow = (label, value, indent = 0, bold = false, deduction = false) => {
          const display = deduction ? `(${formatCurrency(value)})` : formatCurrency(value);
          const row = ws.addRow([`${'  '.repeat(indent)}${label}`, display]);
          if (bold) row.font = { bold: true };
        };
        const addSection = (label) => {
          const row = ws.addRow([label, '']);
          row.eachCell((c) => Object.assign(c, sectionStyle));
        };

        addSection(t('report.pnlReport.revenue'));
        if (showEcomSection) {
          addRow(t('report.pnlReport.ecomSales'), null, 1);
          addRow(t('report.pnlReport.realizedSales'), data.revenue.realizedSales, 2);
          addRow(t('report.pnlReport.unrealizedSales'), data.revenue.unrealizedSales, 2);
          addRow(t('report.pnlReport.salesReturns'), data.revenue.salesReturns, 2, false, true);
        }
        addRow(t('report.pnlReport.posSales'), data.revenue.posSales, 1);
        addRow(t('report.pnlReport.lessPosReturns'), data.revenue.posReturns, 1, false, true);
        addRow(t('report.pnlReport.netRevenue'), data.revenue.netRevenue, 0, true);

        addSection(t('report.pnlReport.cogs'));
        if (useStockCogs) {
          addRow(t('report.pnlReport.openingStock'), data.cogs.openingStock, 1);
          addRow(t('report.pnlReport.addPurchases'), data.cogs.purchases, 1);
          addRow(t('report.pnlReport.lessClosingStock'), data.cogs.closingStock, 1, false, true);
        } else {
          addRow(t('report.pnlReport.posProductCost'), data.cogs.posProductCost, 1);
        }
        addRow(t('report.pnlReport.cogsTotal'), data.cogs.cogs, 0, true);

        addRow(t('report.pnlReport.grossProfit'), data.grossProfit, 0, true);
        ws.addRow([]);

        addSection(t('report.pnlReport.operatingExpenses'));
        if (data.courierCharges > 0) {
          addRow(t('report.pnlReport.courierCharges'), data.courierCharges, 1);
        }
        if (data.posFees > 0) {
          addRow(t('report.pnlReport.posFees'), data.posFees, 1);
        }
        data.expenses.forEach((cat) => {
          addRow(cat.categoryName, cat.amount, 1);
          cat.children.forEach((child) => addRow(child.categoryName, child.amount, 2));
        });
        addRow(t('report.pnlReport.totalExpenses'), data.totalExpenses, 0, true);

        addRow(data.netProfit >= 0 ? t('report.pnlReport.netProfit') : t('report.pnlReport.netLoss'), Math.abs(data.netProfit), 0, true);
      } else {
        // Grouped export: periods as columns
        const { periods, totals } = reportData;
        const allCols = [...periods, { ...totals, label: t('report.pnlReport.total') }];
        const headers = ['', ...allCols.map((c) => c.label)];
        ws.addRow(headers).eachCell((c) => Object.assign(c, headerStyle));

        const addGroupRow = (label, getValue, bold = false) => {
          const row = ws.addRow([label, ...allCols.map(getValue)]);
          if (bold) row.font = { bold: true };
        };
        const addGroupSection = (label) => {
          const row = ws.addRow([label, ...allCols.map(() => '')]);
          row.eachCell((c) => Object.assign(c, sectionStyle));
        };

        const fmt = (v) => formatCurrency(v);
        addGroupSection(t('report.pnlReport.revenue'));
        if (showEcomSection) {
          addGroupRow(`  ${t('report.pnlReport.ecomSales')}`, () => '');
          addGroupRow(`    ${t('report.pnlReport.realizedSales')}`, (c) => fmt(c.revenue?.realizedSales ?? 0));
          addGroupRow(`    ${t('report.pnlReport.unrealizedSales')}`, (c) => fmt(c.revenue?.unrealizedSales ?? 0));
          addGroupRow(`    ${t('report.pnlReport.salesReturns')}`, (c) => `(${fmt(c.revenue?.salesReturns ?? 0)})`);
        }
        addGroupRow(`  ${t('report.pnlReport.posSales')}`, (c) => fmt(c.revenue?.posSales ?? 0));
        addGroupRow(`  ${t('report.pnlReport.lessPosReturns')}`, (c) => `(${fmt(c.revenue?.posReturns ?? 0)})`);
        addGroupRow(t('report.pnlReport.netRevenue'), (c) => fmt(c.revenue?.netRevenue ?? 0), true);

        addGroupSection(t('report.pnlReport.cogs'));
        if (useStockCogs) {
          addGroupRow(`  ${t('report.pnlReport.openingStock')}`, (c) => fmt(c.cogs?.openingStock ?? 0));
          addGroupRow(`  ${t('report.pnlReport.addPurchases')}`, (c) => fmt(c.cogs?.purchases ?? 0));
          addGroupRow(`  ${t('report.pnlReport.lessClosingStock')}`, (c) => `(${fmt(c.cogs?.closingStock ?? 0)})`);
        } else {
          addGroupRow(`  ${t('report.pnlReport.posProductCost')}`, (c) => fmt(c.cogs?.posProductCost ?? 0));
        }
        addGroupRow(t('report.pnlReport.cogsTotal'), (c) => fmt(c.cogs?.cogs ?? 0), true);

        addGroupRow(t('report.pnlReport.grossProfit'), (c) => fmt(c.grossProfit ?? 0), true);

        addGroupSection(t('report.pnlReport.operatingExpenses'));
        addGroupRow(`  ${t('report.pnlReport.courierCharges')}`, (c) => fmt(c.courierCharges ?? 0));
        addGroupRow(`  ${t('report.pnlReport.posFees')}`, (c) => fmt(c.posFees ?? 0));
        addGroupRow(t('report.pnlReport.totalExpenses'), (c) => fmt(c.totalExpenses ?? 0), true);
        addGroupRow(t('report.pnlReport.netProfit'), (c) => fmt(c.netProfit ?? 0), true);
      }

      ws.columns.forEach((col) => (col.width = 22));
      ws.getColumn(1).width = 40;

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PnL_${appliedFilters.dateFrom}_${appliedFilters.dateTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showError(t('report.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  // ── Preset button labels ───────────────────────────────────────────────────
  const presetButtons = [
    { key: 'today',       label: t('report.today') },
    { key: 'thisMonth',   label: t('report.thisMonth') },
    { key: 'lastMonth',   label: t('report.lastMonth') },
    { key: 'thisQuarter', label: t('report.pnlReport.thisQuarter') },
    { key: 'lastQuarter', label: t('report.pnlReport.lastQuarter') },
    { key: 'thisYear',    label: t('report.thisYear') },
    { key: 'custom',      label: t('report.customRange') },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <PageHeader
        title={t('report.pnlReport.title')}
        subtitle={t('report.pnlReport.subtitle')}
        icon={TrendingUp}
        actions={
          reportData && (
            <button
              className={styles.exportBtn}
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader2 size={16} className={styles.spin} />
              ) : (
                <Download size={16} />
              )}
              {isExporting ? t('report.pnlReport.exporting') : t('report.pnlReport.exportExcel')}
            </button>
          )
        }
      />

      {/* ── Filter Panel ── */}
      <div className={styles.filterSection}>
        <div className={styles.filterHeader}>
          <div className={styles.filterHeaderLeft}>
            <Filter size={18} />
            <h2>{t('report.filters')}</h2>
          </div>
          <button className={styles.filterToggle} onClick={() => setShowFilters((v) => !v)}>
            {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>

        {showFilters && (
          <div className={styles.filterContent}>
            {/* Date presets */}
            <div className={styles.filterGroup}>
              <label>{t('report.dateRange')}</label>
              {isLoadingFilters ? (
                <div className={styles.loadingRow}>
                  <Loader2 size={16} className={styles.spin} />
                </div>
              ) : (
                <div className={styles.datePresets}>
                  {presetButtons.map(({ key, label }) => (
                    <button
                      key={key}
                      className={[styles.presetBtn, selectedPreset === key ? styles.presetBtnActive : ''].join(' ')}
                      onClick={() => handlePresetChange(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Date inputs */}
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <label>{t('report.dateFrom')}</label>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setSelectedPreset('custom'); }}
                />
              </div>
              <div className={styles.filterGroup}>
                <label>{t('report.dateTo')}</label>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setSelectedPreset('custom'); }}
                />
              </div>
            </div>

            {/* Group by */}
            <div className={styles.filterGroup}>
              <label>{t('report.pnlReport.groupByLabel')}</label>
              <div className={styles.groupByToggle}>
                {[
                  { key: 'none', label: t('report.pnlReport.groupByNone') },
                  { key: 'month', label: t('report.pnlReport.groupByMonth') },
                  { key: 'quarter', label: t('report.pnlReport.groupByQuarter') },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    className={[styles.groupByTab, groupBy === key ? styles.groupByTabActive : ''].join(' ')}
                    onClick={() => setGroupBy(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className={styles.filterActions}>
              <button
                className={styles.applyBtn}
                onClick={handleApply}
                disabled={isLoading || !dateFrom || !dateTo}
              >
                {isLoading ? (
                  <Loader2 size={16} className={styles.spin} />
                ) : (
                  <RefreshCw size={16} />
                )}
                {isLoading ? t('report.pnlReport.generating') : t('report.pnlReport.generateReport')}
              </button>
              <button className={styles.resetBtn} onClick={handleReset} disabled={isLoading}>
                {t('report.resetFilters')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Report Area ── */}
      {isLoading && (
        <div className={styles.loadingState}>
          <Loader2 size={32} className={styles.spin} />
          <p>{t('report.loadingData')}</p>
        </div>
      )}

      {!isLoading && !reportData && (
        <div className={styles.emptyState}>
          <TrendingUp size={48} className={styles.emptyIcon} />
          <p>{t('report.pnlReport.selectFiltersPrompt')}</p>
        </div>
      )}

      {!isLoading && reportData && (
        <div className={styles.reportCard}>
          {/* Report header */}
          <div className={styles.reportCardHeader}>
            <div className={styles.reportMeta}>
              <span className={styles.dateBadge}>
                {appliedFilters.dateFrom} — {appliedFilters.dateTo}
              </span>
              {appliedFilters.groupBy !== 'none' && (
                <span className={styles.groupBadge}>
                  {appliedFilters.groupBy === 'month'
                    ? t('report.pnlReport.groupByMonth')
                    : t('report.pnlReport.groupByQuarter')}
                </span>
              )}
            </div>

            {/* Net profit summary chip */}
            {(() => {
              const np = reportData.groupBy === 'none'
                ? reportData.data?.netProfit
                : reportData.totals?.netProfit;
              return (
                <div className={[styles.netProfitChip, np >= 0 ? styles.profitChip : styles.lossChip].join(' ')}>
                  <span className={styles.netProfitChipLabel}>
                    {np >= 0 ? t('report.pnlReport.netProfit') : t('report.pnlReport.netLoss')}
                  </span>
                  <span className={styles.netProfitChipAmount}>
                    {formatCurrency(Math.abs(np))}
                  </span>
                </div>
              );
            })()}
          </div>

          {/* Report body */}
          {reportData.groupBy === 'none' ? (
            <PnlSummaryTable
              data={reportData.data}
              showEcomSection={reportData.showEcomSection}
              useStockCogs={reportData.useStockCogs}
              t={t}
            />
          ) : (
            <PnlGroupedTable data={reportData} t={t} />
          )}
        </div>
      )}
    </div>
  );
};

export default PnlReportPage;
